import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  CheckCircle2,
  Loader2,
  Package,
  Pencil,
  Plus,
} from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { adminCatalogApi, adminCatalogKey } from '@/lib/admin-catalog-api';
import { getErrorMessage } from '@/lib/errors';
import { advertisedPriceMinor, formatMinor } from '@/modules/admin/catalog/advertised-price';
import { PlanDialog } from '@/modules/admin/catalog/PlanDialog';
import { ProductDialog } from '@/modules/admin/catalog/ProductDialog';
import type { CatalogStatus, Plan, Product } from '@/types/api';

const statusTone: Record<CatalogStatus, 'success' | 'warning' | 'muted'> = {
  active: 'success',
  draft: 'warning',
  archived: 'muted',
};

/**
 * Products, plans, and publishing them.
 *
 * ## This screen was read-only until 2026-08-08, and that was a blocker
 *
 * The endpoints had existed since the module shipped; only the UI was missing, so the catalog
 * could be *read* here and changed only over the API. That is tolerable for a module nobody
 * needs yet — and untenable for the first thing an operator must do, because **nothing works
 * for any customer until an active plan exists.** A subscription can only be sold against a
 * published plan, and without one a customer sees 0 / 0 servers.
 *
 * The documentation claimed this was already an editor. It was not. Worth remembering that a
 * screen which renders correctly and does nothing is the hardest kind of gap to notice.
 */
export function AdminCatalogPage() {
  const queryClient = useQueryClient();
  const [editingProduct, setEditingProduct] = useState<Product | null | undefined>(undefined);
  const [planFor, setPlanFor] = useState<{ product: Product; plan: Plan | null } | null>(null);

  const products = useQuery({
    queryKey: adminCatalogKey,
    queryFn: () => adminCatalogApi.list(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: adminCatalogKey });

  const productStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CatalogStatus }) =>
      adminCatalogApi.setProductStatus(id, status),
    onSuccess: refresh,
  });

  const planStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CatalogStatus }) =>
      adminCatalogApi.setPlanStatus(id, status),
    onSuccess: refresh,
  });

  const nothingPublished =
    products.data !== undefined
    && products.data.length > 0
    && !products.data.some(
      (p) => p.status === 'active' && p.plans.some((plan) => plan.status === 'active'),
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">Catalog</h1>
          <p className="text-sm text-muted-foreground">
            Products and the plans subscriptions are sold against.
          </p>
        </div>
        <Button className="ml-auto" onClick={() => setEditingProduct(null)}>
          <Plus className="mr-2 size-4" />
          New product
        </Button>
      </div>

      {(productStatus.isError || planStatus.isError) && (
        <Alert variant="danger">
          {getErrorMessage(productStatus.error ?? planStatus.error)}
        </Alert>
      )}

      {/* Said plainly, because the consequence is invisible from anywhere else in the product:
          a customer with no active plan to subscribe to simply sees nothing work. */}
      {nothingPublished && (
        <Alert variant="warning">
          Nothing is published yet. A subscription can only be sold against an <strong>active</strong>{' '}
          plan under an <strong>active</strong> product. Until then customers see 0 / 0 servers
          and cannot add any.
        </Alert>
      )}

      {products.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading catalog…
        </p>
      )}

      {products.isError && <Alert variant="danger">{getErrorMessage(products.error)}</Alert>}

      {products.data?.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Package className="size-8 text-muted-foreground" />
            <p className="font-medium">No products yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              Create a product, add a plan with a <code className="font-mono">servers</code>{' '}
              entitlement, and set both to active. Customers cannot use the platform until you
              have.
            </p>
            <Button className="mt-1" onClick={() => setEditingProduct(null)}>
              <Plus className="mr-2 size-4" />
              Create your first product
            </Button>
          </CardContent>
        </Card>
      )}

      {products.data?.map((product) => (
        <Card key={product.id}>
          {/* The same two columns as a plan row. The controls used to sit beside the text at every
              width, and on a phone they took the width and left the product's name and description
              a column one word wide, with the status badge on top of the name. */}
          <CardHeader className="flex flex-col gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0 flex-1">
              <CardTitle>{product.name}</CardTitle>
              <CardDescription>
                <span className="font-mono text-xs">{product.slug}</span>
                {product.description && ` · ${product.description}`}
              </CardDescription>
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2">
              <Badge variant={statusTone[product.status]}>{product.status}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setEditingProduct(product)}>
                <Pencil className="mr-1.5 size-3.5" />
                Edit
              </Button>
              <StatusButton
                status={product.status}
                busy={productStatus.isPending}
                onChange={(status) => productStatus.mutate({ id: product.id, status })}
              />
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            {product.plans.length === 0 ? (
              <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                No plans yet. A product without one cannot be sold.
              </p>
            ) : (
              product.plans.map((plan) => (
                <PlanRow
                  key={plan.id}
                  plan={plan}
                  productSlug={product.slug}
                  busy={planStatus.isPending}
                  onEdit={() => setPlanFor({ product, plan })}
                  onStatus={(status) => planStatus.mutate({ id: plan.id, status })}
                />
              ))
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPlanFor({ product, plan: null })}
            >
              <Plus className="mr-1.5 size-3.5" />
              Add plan
            </Button>
          </CardContent>
        </Card>
      ))}

      {/* `undefined` means closed, `null` means "create" — a plain null could not tell the
          two apart, and the dialog needs to distinguish creating from editing. */}
      {editingProduct !== undefined && (
        <ProductDialog product={editingProduct} onClose={() => setEditingProduct(undefined)} />
      )}

      {planFor !== null && (
        <PlanDialog
          productId={planFor.product.id}
          productName={planFor.product.name}
          plan={planFor.plan}
          onClose={() => setPlanFor(null)}
        />
      )}
    </div>
  );
}

/**
 * Publish and archive.
 *
 * Only ever offers the one move that makes sense from here, rather than a dropdown of every
 * status. Publishing is the act with consequences — it is what makes a plan sellable — so it
 * gets its own button and its own words.
 */
function StatusButton({
  status,
  busy,
  onChange,
}: {
  status: CatalogStatus;
  busy: boolean;
  onChange: (status: CatalogStatus) => void;
}) {
  if (status === 'active') {
    return (
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onChange('archived')}>
        <Archive className="mr-1.5 size-3.5" />
        Archive
      </Button>
    );
  }

  return (
    <Button variant="outline" size="sm" disabled={busy} onClick={() => onChange('active')}>
      <CheckCircle2 className="mr-1.5 size-3.5" />
      {status === 'draft' ? 'Publish' : 'Restore'}
    </Button>
  );
}

function PlanRow({
  plan,
  productSlug,
  busy,
  onEdit,
  onStatus,
}: {
  plan: Plan;
  productSlug: string;
  busy: boolean;
  onEdit: () => void;
  onStatus: (status: CatalogStatus) => void;
}) {
  const grantsServers = 'servers' in plan.features;

  // Two things that are wrong in a way nothing else reports. A price the public page disagrees
  // with, and a live plan no gateway can actually sell — both let a customer reach a checkout
  // that does the wrong thing while every part of the system reports success.
  const advertised = advertisedPriceMinor(plan, productSlug);
  const priceDisagrees = advertised !== null && advertised !== plan.price_minor;
  // The free trial is granted, never sold, so having no gateway price id is correct for it.
  const unsellable = !plan.trial && plan.status === 'active' && Object.keys(plan.gateway_price_ids).length === 0;

  return (
    // Two columns that never wrap into each other from a small screen up: the details wrap inside
    // their own column and the controls stay on the right. With one wrapping row, the free trial's
    // long description pushed its controls onto a line of their own, under the text on the left.
    <div className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{plan.name}</p>
        <p className="text-xs text-muted-foreground">
          {plan.trial
            ? `Free trial · ${plan.trial_days} days`
            : `${plan.currency} ${plan.price_formatted} · ${plan.billing_interval_label}${plan.trial_days > 0 ? ` · ${plan.trial_days}-day trial` : ''}`}
        </p>

        {plan.trial && (
          <p className="mt-1.5 text-xs text-muted-foreground">
            New accounts start on this when they confirm their email address, and customers who have never
            had a subscription can start it from their dashboard. It is never sold or listed with the plans.
            The entitlements and days set here apply to every trial, including those already running.
          </p>
        )}

        {Object.keys(plan.features).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {Object.entries(plan.features).map(([key, limit]) => (
              <span
                key={key}
                className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
              >
                {key}: {typeof limit === 'boolean' ? (limit ? 'yes' : 'no') : limit === -1 ? '∞' : limit}
              </span>
            ))}
          </div>
        )}

        {/* Flagged on the row, not only in the editor. Somebody reviewing the catalog months
            later should be able to see the problem without opening anything. */}
        {!grantsServers && (
          <p className="mt-1.5 text-xs text-warning">
            No <code className="font-mono">servers</code> entitlement. Customers on this plan
            cannot add any.
          </p>
        )}

        {priceDisagrees && advertised !== null && (
          <p className="mt-1.5 text-xs text-warning">
            The pricing page advertises{' '}
            <strong>{formatMinor(advertised, plan.currency)}</strong> for this plan; the catalogue
            holds {formatMinor(plan.price_minor, plan.currency)}.{' '}
            <strong>
              Check the price at the gateway too: the checkout charges whatever the price id is
              set to there, not this figure.
            </strong>
          </p>
        )}

        {unsellable && (
          <p className="mt-1.5 text-xs text-warning">
            Live, but no gateway price id. Nothing can be sold on this plan until one is set.
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2">
        <Badge variant={statusTone[plan.status]}>{plan.status}</Badge>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="mr-1.5 size-3.5" />
          Edit
        </Button>
        <StatusButton status={plan.status} busy={busy} onChange={onStatus} />
      </div>
    </div>
  );
}
